import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup, ReactiveFormsModule, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatNativeDateModule } from '@angular/material/core';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSelectModule } from '@angular/material/select';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { MatSnackBar } from '@angular/material/snack-bar';
import { AppStateService } from '../../app-state.service';
import { FormConfigService } from '../forms-config.service';
import { HeaderComponent } from '../../header/header.component';
import { AddressAutocompleteWrapperComponent } from '../../address-auto-complete-wrapper.component';
import { AutoSlashDateFormatDirective } from '../../auto-slash-date-format.directive';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../../environments/environment';

@Component({
  selector: 'app-new-patient',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatCardModule,
    MatButtonModule,
    MatProgressSpinnerModule,
    MatDatepickerModule,
    MatNativeDateModule,
    AutoSlashDateFormatDirective,
    MatIconModule,
    HeaderComponent,
    RouterLink
],
  templateUrl: './new-patient.component.html',
  styleUrls: ['./new-patient.component.scss']
})
export class NewPatientComponent implements OnInit {
  profileForm: FormGroup;
  loading = false;
  clientId: string;
  patientId: string;
  tenantId: string;
  isFormEdited = false;
  hide = true;

  maritalStatuses = ['Single', 'Married', 'Child', 'Widowed', 'Divorced'];
  genders = ['Male', 'Female', 'Unknown'];

  constructor(
    private fb: FormBuilder,
    private appStateService: AppStateService,
    private formConfigService: FormConfigService,
    private snackBar: MatSnackBar,
    private router: Router,
    private route: ActivatedRoute,
    private http: HttpClient
  ) {
    this.clientId = this.appStateService.getState().clientId || '';
    this.patientId = this.appStateService.getState().patientId || '';
    this.tenantId = this.appStateService.getState().tenantId || '';

    this.profileForm = this.fb.group({
      lastName: ['', Validators.required],
      firstName: ['', Validators.required],
      gender: ['Unknown'],
      dateOfBirth: [null, Validators.required],
      phoneNumber: ['', [Validators.required, this.phoneNumberValidator()]],
      email: ['', [Validators.required, Validators.email]],
      textMessageConsent: ['Yes'],
    });

    this.profileForm.valueChanges.subscribe(() => {
      this.isFormEdited = true;
    });
  }

  phoneNumberValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const phoneNumberPattern = /^\+?1?\d{10,12}$/;
      const valid = phoneNumberPattern.test(control.value);
      return valid ? null : { invalidPhoneNumber: { value: control.value } };
    };
  }

  ngOnInit(): void {
    // Check if there's signup data from the signup form
    const signupDataStr = sessionStorage.getItem('signupData');
    if (signupDataStr) {
      try {
        const signupData = JSON.parse(signupDataStr);
        // Pre-populate form with data from signup
        // Format phone number to remove any formatting for the form
        let phoneNumber = signupData.phone.replace(/\D/g, '');
        if (phoneNumber.length === 10) {
          phoneNumber = '+1' + phoneNumber;
        } else if (phoneNumber.length === 11 && phoneNumber.startsWith('1')) {
          phoneNumber = '+' + phoneNumber;
        }
        
        this.profileForm.patchValue({
          firstName: signupData.firstName,
          lastName: signupData.lastName,
          phoneNumber: phoneNumber,
          email: signupData.email,
          dateOfBirth: signupData.dob
        });
        // Clear the signup data to prevent reuse
        sessionStorage.removeItem('signupData');
      } catch (error) {
      }
    }
  }

  adjustDateForTimezone(date: Date): Date {
    date.setMinutes(date.getMinutes() + date.getTimezoneOffset());
    return date;
  }

  convertToDate(dateString: string): Date | null {
    if (!dateString) {
      return null;
    }

    let parsedDate = new Date(dateString);
    if (!isNaN(parsedDate.getTime())) {
      return this.adjustDateForTimezone(parsedDate);
    }

    let dateOnlyString = dateString.split('T')[0].split(' ')[0];
    const [year, month, day] = dateOnlyString.split('-');
    if (year && month && day) {
      return this.adjustDateForTimezone(new Date(Number(year), Number(month) - 1, Number(day)));
    }
    // If all else fails, return null indicating invalid date format
    return null;
  }

  onSave(): void {
    if (this.profileForm.valid && this.isFormEdited) {
      this.loading = true;
      const updatedProfile: any = this.profileForm.getRawValue();
      
      // Format date properly using local timezone to avoid timezone offset issues
      let dateOfBirth = updatedProfile.dateOfBirth;
      if (dateOfBirth instanceof Date) {
        const year = dateOfBirth.getFullYear();
        const month = String(dateOfBirth.getMonth() + 1).padStart(2, '0');
        const day = String(dateOfBirth.getDate()).padStart(2, '0');
        dateOfBirth = `${year}-${month}-${day}`;
      } else if (typeof dateOfBirth === 'string') {
        // If it's already a string, make sure it's in YYYY-MM-DD format
        const date = new Date(dateOfBirth);
        if (!isNaN(date.getTime())) {
          const year = date.getFullYear();
          const month = String(date.getMonth() + 1).padStart(2, '0');
          const day = String(date.getDate()).padStart(2, '0');
          dateOfBirth = `${year}-${month}-${day}`;
        }
      }
      
      // Format phone number - ensure it has +1 prefix
      let phoneNumber = updatedProfile.phoneNumber;
      if (phoneNumber) {
        phoneNumber = phoneNumber.replace(/\D/g, '');
        if (phoneNumber.length === 10) {
          phoneNumber = '+1' + phoneNumber;
        } else if (phoneNumber.length === 11 && phoneNumber.startsWith('1')) {
          phoneNumber = '+' + phoneNumber;
        }
      }
      
      // Add orgId and clientId for backend
      const orgId = this.appStateService.getState().orgId || 'tlnk-def-1f3ce';
      const clientId = this.appStateService.getState().clientId || this.clientId || 'tlnk-brightsmiledental-tx';
      
      const patientDataCopy = {
        firstName: updatedProfile.firstName?.trim(),
        lastName: updatedProfile.lastName?.trim(),
        phoneNumber: phoneNumber,
        email: updatedProfile.email?.trim()?.toLowerCase(),
        dateOfBirth: dateOfBirth,
        gender: updatedProfile.gender || 'Unknown',
        textMessageConsent: updatedProfile.textMessageConsent || 'Yes',
        orgId: orgId,
        clientId: clientId
      };
      

      this.formConfigService.createPatient(clientId, patientDataCopy)
      .subscribe({
        next: (response) => {
          this.snackBar.open('Patient Created successfully', 'Close', {
            duration: 3000,
          });
          if (!response.patientId) {
            this.snackBar.open('Failed to update profile', 'Close', {
              duration: 5000,
            });
            this.loading = false;
            return
          }
          this.patientId = response.patientId;
          sessionStorage.setItem('patientId', this.patientId);
          this.appStateService.setState({
            patientId: this.patientId,
            phoneNumber: updatedProfile.phoneNumber,
            // Clear any appointment data from previous sessions
            appointmentData: null,
            appointmentBooked: false
          });
          this.router.navigate(['/auth']);
        },
        error: error => {
          this.snackBar.open('Failed to update profile', 'Close', {
            duration: 5000,
          });
          this.loading = false;
         }
        }
      );
    } else if (!this.isFormEdited) {
      this.snackBar.open('No changes detected in the form', 'Close', {
        duration: 3000,
      });
    } else {
      this.snackBar.open('Please correct the errors in the form', 'Close', {
        duration: 5000,
      });
    }
  }

  onCancel(): void {
    this.router.navigate(['/login']);
  }
}
